import { z } from 'zod'
import { FirestoreTimestamp } from './utils'

/**
 * UserAccess - Denormalized access control document
 * Computed from user's workRouteIds - provides fast access checks
 *
 * Collection path: companies/{companyId}/userAccess/{userId}
 *
 * This document is automatically maintained by Cloud Functions and should
 * never be modified directly by clients.
 */
export const UserAccess = z.object({
  userId: z.string(),
  companyId: z.string(),

  // Computed from all assigned WorkRoutes
  accessibleSiteIds: z.array(z.string()).default([]),

  // Denormalized route information for UI display
  workRoutes: z
    .record(
      z.string(),
      z.object({
        name: z.string(),
        siteIds: z.array(z.string()),
      }),
    )
    .default({}),

  // Audit trail
  lastUpdated: FirestoreTimestamp,
  updatedBy: z.string(), // 'system' or userId of admin who triggered update
})

export type UserAccess = z.infer<typeof UserAccess>

/**
 * ClientUserAccess - Denormalized access control for client users
 * Computed from user's organizationIds and optionally workRouteIds
 *
 * Stored in: clientUsers/{userId} document (as additional fields, not separate collection)
 *
 * This data is automatically computed by Cloud Functions and should
 * never be modified directly by clients.
 */
export const ClientUserAccess = z.object({
  clientUserId: z.string(),

  // Computed accessible sites (may use AND logic if workRouteIds present)
  accessibleSiteIds: z.array(z.string()).default([]),

  // Denormalized organization information for UI display
  organizations: z
    .record(
      z.string(),
      z.object({
        name: z.string(),
        type: z.enum(['building_owner', 'property_manager', 'facilities_dept']),
        siteCount: z.number(),
        serviceCompanyId: z.string(),
      }),
    )
    .default({}),

  // Audit trail
  lastUpdated: FirestoreTimestamp,
  updatedBy: z.string(), // 'system' or userId of admin who triggered update
})

export type ClientUserAccess = z.infer<typeof ClientUserAccess>
